import { ComponentPropsWithoutRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { markUncollectable as markUncollectableApi } from 'api/campaigns';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Bid } from 'types/domain';

interface MarkUncollectableProps extends ComponentPropsWithoutRef<'div'> {
    bid: Bid;
}

const MarkUncollectableModal = ({ bid, children }: MarkUncollectableProps) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = Number(campaign.id);
    const auctionId = Number(campaign.auction_id);
    const runnerUpBid = bid?.second_chance_offer_bid;
    const currencySymbol = useCurrencySymbol();
    const itemReservePrice = Number(bid?.item_reserve_price);
    const runnerUpBidAmount = Number(runnerUpBid?.amount || 0);
    const sponsoredReservePriceItem = Number(runnerUpBid?.item_reserve_price_sponsor);
    const disableSubmit =
        !!sponsoredReservePriceItem && sponsoredReservePriceItem > runnerUpBidAmount;

    const params = {
        secondChanceBidId: bid?.second_chance_offer_bid?.id,
    };

    const invalidate = () =>
        queryClient.invalidateQueries({
            queryKey: campaignQueryKeys.auction(campaignId, auctionId),
        });

    const { mutate: markUncollectable } = useMutation({
        mutationFn: () => markUncollectableApi(campaignId, auctionId, bid?.id),
        onSuccess: () => {
            toast.success('Successfully marked payment as uncollectable');
            invalidate();
        },
        onError: () => {
            toast.error('Unable to mark payment uncollectable');
        },
    });

    const { mutate: offerSecondChance } = useMutation({
        mutationFn: () => markUncollectableApi(campaignId, auctionId, bid?.id, params),
        onSuccess: () => {
            toast.success(`Successfully offered ${bid?.item_name} to the runner-up bidder`);
            invalidate();
        },
        onError: () => {
            toast.error('Unable to offer item to the runner-up bidder');
        },
    });

    return (
        <AlertDialog.Root type={runnerUpBid ? 'warning' : 'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    {!runnerUpBid && (
                        <>
                            <AlertDialog.Header>
                                <AlertDialog.Icon />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <AlertDialog.Title>Mark Uncollectable?</AlertDialog.Title>
                                <AlertDialog.Description>
                                    Marking a bid as uncollectable is an irreversible action and
                                    will result in the auction item having no winning bidder.
                                </AlertDialog.Description>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <AlertDialog.Action onClick={() => markUncollectable()}>
                                    Confirm
                                </AlertDialog.Action>
                            </AlertDialog.Footer>
                        </>
                    )}
                    {!!runnerUpBid && (
                        <>
                            <AlertDialog.Header>
                                <AlertDialog.Icon />
                                <AlertDialog.Close />
                            </AlertDialog.Header>
                            <AlertDialog.Body>
                                <AlertDialog.Title>
                                    Offer {bid?.item_name} to the runner-up?
                                </AlertDialog.Title>
                                <div className="flex flex-col gap-y-4">
                                    <div className="flex flex-col gap-y-4 divide-y">
                                        <div className="flex flex-col gap-2">
                                            <p>
                                                The original winner's bid will be marked as
                                                uncollectable. If the runner-up doesn't purchase the
                                                item, it will have no winner.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col justify-between pt-4 text-black">
                                        <span>Runner-up details</span>
                                        <div className="flex flex-col gap-y-4 rounded-lg bg-gray-50 p-4">
                                            <div className="flex flex-col">
                                                <span>Runner-up bid</span>
                                                <span className="font-medium">
                                                    {currencySymbol + runnerUpBid?.amount}
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-6 gap-x-2">
                                                <div className="col-span-5">
                                                    <p className="font">Name</p>
                                                    <p className="font-medium">
                                                        {runnerUpBid?.bidder?.first_name +
                                                            ' ' +
                                                            runnerUpBid?.bidder?.last_name}
                                                    </p>
                                                </div>
                                                <div className="col-span-1">
                                                    <p>Bid ID#</p>
                                                    <p className="font-medium">{runnerUpBid?.id}</p>
                                                </div>
                                            </div>
                                            {!sponsoredReservePriceItem &&
                                                !!itemReservePrice &&
                                                itemReservePrice > runnerUpBidAmount && (
                                                    <p className="font-medium text-red-400">
                                                        The runner-up's bid is below the reserve
                                                        price. You can still offer this item to the
                                                        runner-up.
                                                    </p>
                                                )}
                                            {!!sponsoredReservePriceItem &&
                                                sponsoredReservePriceItem > runnerUpBidAmount && (
                                                    <p className="font-medium text-red-400">
                                                        Runner-up bids below the reserve price
                                                        cannot be accepted on a sponsored item.
                                                    </p>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel
                                    onClick={() => markUncollectable()}
                                    className="w-48"
                                >
                                    Mark uncollectable
                                </AlertDialog.Cancel>
                                <AlertDialog.Action
                                    onClick={() => offerSecondChance()}
                                    className={`w-48 disabled:cursor-not-allowed disabled:opacity-50`}
                                    disabled={disableSubmit}
                                >
                                    Send offer
                                </AlertDialog.Action>
                            </AlertDialog.Footer>
                        </>
                    )}
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

export { MarkUncollectableModal };
